import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Pencil,
  Clock,
  BookOpen,
  Tag,
  FileText,
  Copy,
  Ban,
  Check,
  CheckCircle,
  Eye,
  Target,
  Package,
  Users,
  Building,
  ExternalLink,
  Settings,
  Star,
  Play,
  Share2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Course, CourseFamily, CourseMode } from '@/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


interface CourseWithRelations extends Course {
  display_id?: string;
  course_families?: CourseFamily;
}

const CourseDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<CourseWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmInactivate, setConfirmInactivate] = useState(false);
  const [confirmReactivate, setConfirmReactivate] = useState(false);

  useEffect(() => {
    if (id) {
      fetchCourseDetails();
    }
  }, [id]);

  const fetchCourseDetails = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('courses')
        .select(`
          *,
          course_families(*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      
      console.log('CourseDetail - Dados do curso carregados:', data);
      console.log('CourseDetail - Propriedades de modalidade:', {
        mode: data.mode,
        modeType: typeof data.mode,
        isArray: Array.isArray(data.mode)
      });
      console.log('CourseDetail - Todas as propriedades de duração:');
      console.log('- duration_onlineasync:', data.duration_onlineasync, typeof data.duration_onlineasync);
      console.log('- duration_onlinesync:', data.duration_onlinesync, typeof data.duration_onlinesync);
      console.log('- duration_inperson:', data.duration_inperson, typeof data.duration_inperson);
      console.log('- duration_minutes:', data.duration_minutes, typeof data.duration_minutes);
      
      setCourse(data);
    } catch (error: any) {
      toast.error('Erro ao carregar detalhes do curso', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const getSingleModeBadge = (mode: string) => {
    switch (mode) {
      case 'onlineAsync':
        return <Badge variant="outline" className="bg-[#3b65f5]/10 text-[#3b65f5]">Online Assíncrono</Badge>;
      case 'onlineSync':
        return <Badge variant="outline" className="bg-[#ff4a8f]/10 text-[#ff4a8f]">Online Síncrono</Badge>;
      case 'in-person':
        return <Badge variant="outline" className="bg-[#c8e600]/10 text-[#c8e600]">Presencial</Badge>;
      default:
        return <Badge variant="outline">Desconhecido</Badge>;
    }
  };

  const getModeBadge = (mode: string | string[] | null | undefined) => {
    if (!mode) {
      return <Badge variant="outline" className="bg-gray-100 text-gray-800">Não definido</Badge>;
    }

    if (Array.isArray(mode)) {
      if (mode.length === 0) {
        return <Badge variant="outline" className="bg-gray-100 text-gray-800">Não definido</Badge>;
      }
      return (
        <span className="flex flex-wrap gap-2">
          {mode.map((m, index) => (
            <React.Fragment key={index}>
              {getSingleModeBadge(m)}
            </React.Fragment>
          ))}
        </span>
      );
    }
    
    return getSingleModeBadge(mode);
  };

  const getSingleModeLabel = (mode: string) => {
    switch (mode) {
      case 'onlineAsync':
        return 'Online Assíncrono';
      case 'onlineSync':
        return 'Online Síncrono';
      case 'in-person':
        return 'Presencial';
      default:
        return 'Desconhecido';
    }
  };

  const getModeLabel = (mode: string | string[] | null | undefined) => {
    if (!mode) {
      return 'Não definido';
    }

    if (Array.isArray(mode)) {
      if (mode.length === 0) {
        return 'Não definido';
      }
      return mode.map(m => getSingleModeLabel(m)).join(' / ');
    }
    
    return getSingleModeLabel(mode);
  };

  const handleEditCourse = () => {
    navigate(`/courses/${id}/edit`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="flex items-center justify-center h-full">
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="flex flex-col items-center justify-center h-full">
          <p className="text-lg mb-4">Curso não encontrado</p>
          <Button onClick={() => navigate('/courses')}>Voltar para Cursos</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Modern Header with Gradient Background */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
        {/* SVG Background Element */}
        <div className="absolute inset-0 pointer-events-none">
          <svg 
            className="opacity-30"
            width="1690"
            height="1026.5433"
            viewBox="0 0 826.54 502.06" 
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
          >
            <g transform="scale(1.1) translate(-70, -315)">
              <path d="M0 0h826.54v502.06H0z" fill="none"/>
              <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
              <defs>
                <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity=".1"/>
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity=".1"/>
                </linearGradient>
              </defs>
            </g>
          </svg>
        </div>
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
        
        <div className="relative container mx-auto px-6 py-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
          <Button 
                  variant="ghost" 
            size="icon" 
            onClick={() => navigate('/courses')}
                  className="rounded-full bg-white/10 hover:bg-white/20 text-white border-white/20"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white">{course.name}</h1>
                  <p className="text-blue-100 text-base pb-2">
                    Detalhes e informações do curso
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant="secondary" 
                size="default" 
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success('Link copiado!');
                }}
                className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Compartilhar
              </Button>
            <Button 
                variant="secondary" 
                size="default" 
              onClick={() => navigate(`/courses/${id}/edit`)}
                className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
            >
                <Pencil className="h-4 w-4 mr-2" />
                Editar
            </Button>
            {course?.is_active !== false ? (
              <Button 
                variant="destructive" 
                onClick={() => setConfirmInactivate(true)}
                  className="bg-red-500/80 hover:bg-red-600/80 text-white shadow-lg"
              >
                  <Ban className="h-4 w-4 mr-2" />
                  Inativar
              </Button>
            ) : (
              <Button 
                variant="default" 
                onClick={() => setConfirmReactivate(true)}
                  className="bg-green-500/80 hover:bg-green-600/80 text-white shadow-lg"
              >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Reativar
              </Button>
            )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10">
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <p>Carregando detalhes do curso...</p>
          </div>
        ) : course ? (
          <div className="space-y-8">
            {/* Enhanced Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-100 rounded-md">
                        <Tag className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Família</span>
                    </div>
                  </div>
                  <div className="text-lg font-extrabold text-gray-900 mb-2">
                    {course.course_families?.name || 'Sem família'}
                  </div>
                  <div className="text-xs text-gray-500">
                    Categoria do curso
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-100 rounded-md">
                        <Clock className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Duração</span>
                    </div>
                  </div>
                  <div className="text-lg font-extrabold text-gray-900 mb-2">
                    {course.duration_minutes ? `${course.duration_minutes} min` : 'Não definida'}
                  </div>
                  <div className="text-xs text-gray-500">
                    Tempo estimado
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-purple-100 rounded-md">
                        <Play className="h-4 w-4 text-purple-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Modalidades</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {getModeBadge(course.mode)}
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-orange-100 rounded-md">
                        <Settings className="h-4 w-4 text-orange-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Status</span>
                    </div>
                  </div>
                  <div className="text-lg font-extrabold text-gray-900 mb-2">
                    {course.is_active !== false ? 'Ativo' : 'Inativo'}
                  </div>
                  <div className="text-xs text-gray-500">
                    Estado atual
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Hero Section with Cover Image */}
            <Card className="border shadow-sm bg-white/90 backdrop-blur-sm overflow-hidden">
              <div className="relative h-80 md:h-96">
              {course.cover_url ? (
                <img 
                  src={course.cover_url} 
                  alt={course.name} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://placehold.co/1200x400?text=Imagem+do+Curso';
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#3b65f5] via-[#ff4a8f] to-[#c8e600] flex items-center justify-center">
                  <BookOpen className="h-24 w-24 text-white/30" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              
              {/* Course Header Info */}
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <div className="flex flex-wrap gap-3 mb-4">
                    {course.level && (
                      <Badge variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-white/30">
                        {course.level}
                      </Badge>
                    )}
                    {!course.is_active && (
                      <Badge variant="secondary" className="bg-red-500/80 text-white border-red-400">
                        Inativo
                      </Badge>
                    )}
                    {course.course_number && (
                      <div className="flex items-center gap-2">
                        <span className="font-mono bg-white/20 px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                          {course.course_number}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-white hover:text-white hover:bg-white/20 rounded-full"
                          onClick={() => {
                            navigator.clipboard.writeText(course.course_number || '');
                            toast.success('Código copiado!');
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">{course.name}</h1>
                  
                  {course.description && (
                    <p className="text-white/90 text-lg max-w-3xl leading-relaxed">
                      {course.description.length > 200 
                        ? `${course.description.substring(0, 200)}...` 
                        : course.description
                      }
                    </p>
                  )}
                </div>
              </div>
            </Card>

            {/* Enhanced Main Content */}
            <div className="space-y-8">
                {/* Grid Layout com informações principais */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  
                  {/* Coluna Principal - Informações do Curso */}
                  <div className="lg:col-span-2 space-y-8">
                    
                    {/* Visão Geral e Descrição */}
                  <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                          <Eye className="h-6 w-6 text-white" />
                          </div>
                          <div>
                          <CardTitle className="text-2xl text-gray-900">Visão Geral</CardTitle>
                          <CardDescription className="text-gray-600">Descrição geral e objetivos do curso</CardDescription>
                        </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {course.overview && (
                          <div>
                            <h4 className="font-extrabold text-gray-900 mb-3">Sobre o Curso</h4>
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                              {course.overview}
                            </p>
                          </div>
                        )}
                        
                        {course.description && (
                          <div>
                            <h4 className="font-extrabold text-gray-900 mb-3">Descrição Detalhada</h4>
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                              {course.description}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Habilidades Desenvolvidas */}
                  <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                          <Target className="h-6 w-6 text-white" />
                          </div>
                          <div>
                          <CardTitle className="text-2xl text-gray-900">Habilidades Desenvolvidas</CardTitle>
                          <CardDescription className="text-gray-600">Competências e conhecimentos que serão adquiridos</CardDescription>
                        </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {course.skills_developed ? (
                          <div className="bg-green-50 p-4 rounded-lg">
                            <div className="space-y-3">
                              {course.skills_developed.split('\n').filter(skill => skill.trim()).map((skill, index) => (
                                <div key={index} className="flex gap-3 items-start">
                                  <div className="h-2 w-2 bg-green-500 rounded-full mt-2 shrink-0" />
                                  <span className="text-gray-700 leading-relaxed">{skill.trim()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : course.learning_goals ? (
                          <div className="bg-green-50 p-4 rounded-lg">
                            <div className="space-y-3">
                              {course.learning_goals.split('\n').filter(goal => goal.trim()).map((goal, index) => (
                                <div key={index} className="flex gap-3 items-start">
                                  <div className="h-2 w-2 bg-green-500 rounded-full mt-2 shrink-0" />
                                  <span className="text-gray-700 leading-relaxed">{goal.trim()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Target className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Nenhuma habilidade específica definida.</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Recursos Necessários */}
                  <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                          <Package className="h-6 w-6 text-white" />
                          </div>
                          <div>
                          <CardTitle className="text-2xl text-gray-900">Recursos Necessários</CardTitle>
                          <CardDescription className="text-gray-600">Materiais e recursos necessários para o curso</CardDescription>
                        </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {course.required_resources ? (
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="space-y-3">
                              {course.required_resources.split('\n').filter(resource => resource.trim()).map((resource, index) => (
                                <div key={index} className="flex gap-3 items-start">
                                  <div className="h-2 w-2 bg-blue-500 rounded-full mt-2 shrink-0" />
                                  <span className="text-gray-700 leading-relaxed">{resource.trim()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : course.requirements ? (
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="space-y-3">
                              {course.requirements.split('\n').filter(requirement => requirement.trim()).map((requirement, index) => (
                                <div key={index} className="flex gap-3 items-start">
                                  <div className="h-2 w-2 bg-blue-500 rounded-full mt-2 shrink-0" />
                                  <span className="text-gray-700 leading-relaxed">{requirement.trim()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Nenhum recurso específico requerido.</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Orientações para Participante */}
                  <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                          <Users className="h-6 w-6 text-white" />
                          </div>
                          <div>
                          <CardTitle className="text-2xl text-gray-900">Orientações para o Participante</CardTitle>
                          <CardDescription className="text-gray-600">Instruções importantes para os participantes</CardDescription>
                        </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {course.participant_guidelines ? (
                          <div className="whitespace-pre-line text-gray-700 leading-relaxed bg-purple-50 p-4 rounded-lg">
                            {course.participant_guidelines}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Nenhuma orientação específica para participantes.</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Orientações para Instituição */}
                  <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
                          <Building className="h-6 w-6 text-white" />
                          </div>
                          <div>
                          <CardTitle className="text-2xl text-gray-900">Orientações para a Instituição</CardTitle>
                          <CardDescription className="text-gray-600">Requisitos para a instituição organizadora</CardDescription>
                        </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {course.institution_guidelines ? (
                          <div className="whitespace-pre-line text-gray-700 leading-relaxed bg-[#ff4a8f]/5 p-4 rounded-lg">
                            {course.institution_guidelines}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Building className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Nenhuma orientação específica para instituições.</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Sidebar - Informações Complementares */}
                  <div className="space-y-6">
                    
                    {/* Thumbnail Preview */}
                  <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                          <FileText className="h-5 w-5 text-white" />
                        </div>
                        <CardTitle className="text-lg text-gray-900">Thumbnail do Curso</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {course.thumbnail_url ? (
                          <div className="aspect-video w-full rounded-lg overflow-hidden border bg-muted">
                            <img 
                              src={course.thumbnail_url} 
                              alt={`Thumbnail do curso ${course.name}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://placehold.co/600x400?text=Thumbnail+do+Curso';
                              }}
                            />
                          </div>
                        ) : (
                          <div className="aspect-video w-full rounded-lg overflow-hidden border bg-muted flex items-center justify-center">
                            <BookOpen className="h-12 w-12 text-muted-foreground/40" />
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Duração por Modalidade */}
                  <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                          <Clock className="h-5 w-5 text-white" />
                        </div>
                        <CardTitle className="text-lg text-gray-900">Duração por Modalidade</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {course.duration_onlineasync && course.duration_onlineasync > 0 ? (
                          <div className="flex justify-between items-center p-3 bg-[#3b65f5]/5 rounded-lg border border-[#3b65f5]/20">
                            <span className="text-[#3b65f5] font-extrabold text-sm">Online Assíncrono</span>
                            <span className="text-[#3b65f5] font-extrabold">{course.duration_onlineasync}min</span>
                          </div>
                        ) : null}
                        
                        {course.duration_onlinesync && course.duration_onlinesync > 0 ? (
                          <div className="flex justify-between items-center p-3 bg-[#ff4a8f]/5 rounded-lg border border-[#ff4a8f]/20">
                            <span className="text-[#ff4a8f] font-extrabold text-sm">Online Síncrono</span>
                            <span className="text-[#ff4a8f] font-extrabold">{course.duration_onlinesync}min</span>
                          </div>
                        ) : null}
                        
                        {course.duration_inperson && course.duration_inperson > 0 ? (
                          <div className="flex justify-between items-center p-3 bg-[#c8e600]/5 rounded-lg border border-[#c8e600]/20">
                            <span className="text-[#c8e600] font-extrabold text-sm">Presencial</span>
                            <span className="text-[#c8e600] font-extrabold">{course.duration_inperson}min</span>
                          </div>
                        ) : null}
                        
                        {/* Mostrar mensagem se não houver nenhuma duração definida */}
                        {(!course.duration_onlineasync || course.duration_onlineasync <= 0) && 
                         (!course.duration_onlinesync || course.duration_onlinesync <= 0) && 
                         (!course.duration_inperson || course.duration_inperson <= 0) && (
                          <div className="text-center py-8">
                            <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Nenhuma duração específica definida por modalidade.</p>
                            {course.duration_minutes && course.duration_minutes > 0 && (
                              <p className="text-sm text-gray-400 mt-2">
                                Duração geral: {course.duration_minutes} minutos
                              </p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Materiais do Curso */}
                    {(course.lesson_plan_url || course.lesson_slides_url) && (
                    <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
                        <CardHeader className="pb-4">
                          <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                            <FileText className="h-5 w-5 text-white" />
                          </div>
                          <CardTitle className="text-lg text-gray-900">Materiais do Curso</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {course.lesson_plan_url && (
                            <Button variant="outline" className="w-full justify-start" asChild>
                              <a href={course.lesson_plan_url} target="_blank" rel="noopener noreferrer">
                                <FileText className="h-4 w-4 mr-2" />
                                Plano de Aula
                                <ExternalLink className="h-4 w-4 ml-auto" />
                              </a>
                            </Button>
                          )}
                          {course.lesson_slides_url && (
                            <Button variant="outline" className="w-full justify-start" asChild>
                              <a href={course.lesson_slides_url} target="_blank" rel="noopener noreferrer">
                                <FileText className="h-4 w-4 mr-2" />
                                Slides da Aula
                                <ExternalLink className="h-4 w-4 ml-auto" />
                              </a>
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Ações Rápidas */}
                  <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
                      <CardHeader className="pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                          <Star className="h-5 w-5 text-white" />
                        </div>
                        <CardTitle className="text-lg text-gray-900">Ações Rápidas</CardTitle>
                      </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Button 
                          variant="outline" 
                        className="w-full justify-start bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200 hover:from-blue-100 hover:to-purple-100"
                          onClick={() => {
                            navigator.clipboard.writeText(window.location.href);
                            toast.success('Link copiado!');
                          }}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Compartilhar Curso
                        </Button>
                      </CardContent>
                    </Card>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="space-y-6">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                <BookOpen className="h-12 w-12 text-blue-500" />
              </div>
              <div className="space-y-3">
                <h3 className="text-xl font-extrabold text-gray-900">
                  Curso não encontrado
                </h3>
                <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                  O curso que você está procurando pode ter sido removido ou não existe.
                </p>
              </div>
              <Button onClick={() => navigate('/courses')} size="lg">
                    Voltar para lista de cursos
                  </Button>
                </div>
          </div>
        )}
      </div>
      
      {/* Bottom Spacing */}
      <div className="h-16" />

        {/* Alert Dialogs */}
      <>
        <AlertDialog open={confirmInactivate} onOpenChange={setConfirmInactivate}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Inativar Curso</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja inativar o curso 
                <span className="font-extrabold"> {course?.name}</span>? 
                O curso ficará indisponível para novas formações, mas as formações existentes não serão afetadas.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={async () => {
                  try {
                    const { error } = await supabase
                      .from('courses')
                      .update({ is_active: false })
                      .eq('id', id);

                    if (error) throw error;

                    toast.success('Curso inativado com sucesso');
                    setConfirmInactivate(false);
                    fetchCourseDetails(); // Refresh course data to update UI
                  } catch (error) {
                    console.error('Erro ao inativar curso:', error);
                    toast.error('Não foi possível inativar o curso');
                  }
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Inativar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={confirmReactivate} onOpenChange={setConfirmReactivate}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reativar Curso</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja reativar o curso 
                <span className="font-extrabold"> {course?.name}</span>? 
                O curso ficará disponível novamente para novas formações.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={async () => {
                  try {
                    const { error } = await supabase
                      .from('courses')
                      .update({ is_active: true })
                      .eq('id', id);

                    if (error) throw error;

                    toast.success('Curso reativado com sucesso');
                    setConfirmReactivate(false);
                    fetchCourseDetails(); // Refresh course data to update UI
                  } catch (error) {
                    console.error('Erro ao reativar curso:', error);
                    toast.error('Não foi possível reativar o curso');
                  }
                }}
                className="bg-green-600 text-white hover:bg-green-700"
              >
                Reativar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
      </div>
  );
};

export default CourseDetail;